import { Group, Stack } from "@mantine/core";
import { IconFileTypePdf } from "@tabler/icons-react";
import { useState } from "react";
import { Flag } from "../../../hooks/useFeatureFlags.types";
import { useProject } from "../../../hooks/useProject";
import { IconButton } from "../../../mantine/components/IconButton/IconButton";
import { ExpressOnboardingBox } from "../../../modules/ExpressOnboarding/ExpressOnboardingBox/ExpressOnboardingBox";
import type { SiteWizard } from "../../../utils/backend-types";
import { FeatureFlag } from "../../FeatureFlag/FeatureFlag";
import { useMenuPermissions } from "../../Menu/useMenuPermissions";
import { SiteTasksBox } from "../components/SiteTasksBox";
import { OnboardingAssignment } from "./OnboardingAssignment/OnboardingAssignment";
import { OnboardingDocuments } from "./OnboardingDocuments/OnboardingDocuments";
import { AssignmentType } from "./OnboardingWizard.constants";

export interface OnboardingWizardProps {
  siteWizard: SiteWizard;
  onSiteWizardUpdated: () => Promise<void>;
  projectId: string;
  siteIsFullFeedin?: boolean;
}

function OnboardingWizard({
  siteWizard,
  onSiteWizardUpdated,
  projectId,
  siteIsFullFeedin
}: OnboardingWizardProps) {
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const { project } = useProject(projectId);

  const { revenueMonitoringEnabled } = useMenuPermissions({});

  // the order of OnboardingPhases below should be the same as in the ONBOARDING_PHASES constant, otherwise the nextPhase logic will be confusing with a phase being displayed as next but not being next in the list of assignments to do
  return (
    <SiteTasksBox>
      {documentModalOpen && project?.documentsInOnboardingAssistantActive && (
        <OnboardingDocuments
          siteId={siteWizard.id}
          onClose={() => setDocumentModalOpen(false)}
        />
      )}
      <Stack gap="xl">
        {project?.documentsInOnboardingAssistantActive && (
          <Group justify="flex-end" mb="md">
            <IconButton
              color="neutral"
              icon={IconFileTypePdf}
              variant="subtle"
              onClick={() => setDocumentModalOpen(true)}
            >
              Dokumente
            </IconButton>
          </Group>
        )}
        <OnboardingAssignment
          projectId={projectId}
          siteWizard={siteWizard}
          type={AssignmentType.Configuration}
          onSiteWizardUpdated={onSiteWizardUpdated}
        />
        <OnboardingAssignment
          projectId={projectId}
          siteWizard={siteWizard}
          type={AssignmentType.MeteringConcept}
          onSiteWizardUpdated={onSiteWizardUpdated}
        />
        <OnboardingAssignment
          projectId={projectId}
          siteWizard={siteWizard}
          type={AssignmentType.Regulatory}
          onSiteWizardUpdated={onSiteWizardUpdated}
        />

        <FeatureFlag featureFlag={Flag.ShowErzeugerExpress}>
          {revenueMonitoringEnabled && (
            <ExpressOnboardingBox siteId={siteWizard.id} />
          )}
        </FeatureFlag>

        <OnboardingAssignment
          projectId={projectId}
          siteWizard={siteWizard}
          type={AssignmentType.Contracts}
          onSiteWizardUpdated={onSiteWizardUpdated}
        />
        <OnboardingAssignment
          projectId={projectId}
          siteWizard={siteWizard}
          type={AssignmentType.Invoices}
          onSiteWizardUpdated={onSiteWizardUpdated}
        />
        <OnboardingAssignment
          projectId={projectId}
          siteWizard={siteWizard}
          type={AssignmentType.Vollmachten}
          onSiteWizardUpdated={onSiteWizardUpdated}
        />
        {!siteIsFullFeedin && (
          <OnboardingAssignment
            projectId={projectId}
            siteWizard={siteWizard}
            type={AssignmentType.OptiNode}
            onSiteWizardUpdated={onSiteWizardUpdated}
          />
        )}
      </Stack>
    </SiteTasksBox>
  );
}

export { OnboardingWizard };
